import { toastServerError } from "helpers";
import { useGetAllCategories } from "modules/categories";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Modal, Select } from "ui";

import {
  useAvailableEnrichmentProfiles,
  useSetEnrichmentProfile
} from "../../apis";
import { ENRICHMENT_MODE_LABELS, GENERAL_PROFILE_KEY } from "../../constants";
import { buildCategoryOptions, flattenCategoryTree } from "../../helpers";
import { EEnrichmentMode } from "../../recommendation.types";

import styles from "./EnrichmentProfileForm.module.scss";

const MODE_OPTIONS = Object.values(EEnrichmentMode).map((mode) => ({
  value: mode,
  label: ENRICHMENT_MODE_LABELS[mode]
}));

const EnrichmentProfileForm = () => {
  const { t } = useTranslation();

  const { categories } = useGetAllCategories();
  // Every level, unlike the affinity picker: a rule set on a main category is
  // the point — everything beneath it inherits until a descendant overrides.
  const categoryOptions = useMemo(
    () => buildCategoryOptions(flattenCategoryTree(categories)),
    [categories]
  );

  const { availableProfiles } = useAvailableEnrichmentProfiles();
  const profileOptions = useMemo(
    () =>
      availableProfiles.map((profile) => ({
        value: profile.key,
        label: profile.label
      })),
    [availableProfiles]
  );

  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [mode, setMode] = useState<EEnrichmentMode>(EEnrichmentMode.Vision);
  const [profileKey, setProfileKey] = useState<string>(GENERAL_PROFILE_KEY);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Each schema states the input it was written for — beauty reads the
  // description, general reads the photo — so picking one preselects its mode.
  useEffect(() => {
    const picked = availableProfiles.find(
      (profile) => profile.key === profileKey
    );

    if (picked) {
      setMode(picked.defaultMode);
    }
  }, [profileKey, availableProfiles]);

  const { mutate: setProfile, isLoading } = useSetEnrichmentProfile({
    onSuccess: () => {
      toast.success(t("Enrichment rule saved"));
      setCategoryId(null);
      setIsConfirmOpen(false);
    },
    onError: (error) => {
      setIsConfirmOpen(false);
      toastServerError(error);
    }
  });

  const save = () => {
    if (!categoryId) {
      return;
    }

    setProfile({
      categoryId,
      mode,
      profileKey: mode === EEnrichmentMode.None ? undefined : profileKey
    });
  };

  const handleSave = () => {
    if (!categoryId) {
      toast.error(t("Pick a category"));
      return;
    }

    // `none` erases what is already stored beneath the branch, so it is not a
    // setting you can undo by switching back.
    if (mode === EEnrichmentMode.None) {
      setIsConfirmOpen(true);
      return;
    }

    save();
  };

  const selectedFields = availableProfiles.find(
    (profile) => profile.key === profileKey
  )?.fields;

  return (
    <>
      <div className={styles.form}>
        <div className={styles.form__field}>
          <Select
            label={t("Category branch")}
            placeholder={t("Pick a category")}
            options={categoryOptions}
            value={categoryId}
            withFilterInput
            onChange={(selected) => setCategoryId(selected as string)}
          />
        </div>

        <div className={styles.form__field}>
          <Select
            label={t("Mode")}
            options={MODE_OPTIONS}
            value={mode}
            onChange={(selected) => setMode(selected as EEnrichmentMode)}
          />
        </div>

        <div className={styles.form__field}>
          <Select
            label={t("Attribute set")}
            options={profileOptions}
            value={profileKey}
            disabled={mode === EEnrichmentMode.None}
            onChange={(selected) => setProfileKey(selected as string)}
          />
        </div>

        <Button
          className={styles.form__submit}
          loading={isLoading}
          onClick={handleSave}
        >
          {t("Save rule")}
        </Button>
      </div>

      {mode !== EEnrichmentMode.None && selectedFields?.length ? (
        <p className={styles.fields}>
          {t("Fields asked for")}: {selectedFields.join(", ")}
        </p>
      ) : null}

      <Modal
        type="delete"
        isOpen={isConfirmOpen}
        isLoading={isLoading}
        title={t("Stop tagging this branch?")}
        okText={t("Stop tagging")}
        onClose={() => setIsConfirmOpen(false)}
        onOk={save}
      >
        <p className={styles.confirmText}>
          {t(
            "Attributes already stored for products under this branch will be erased. That is deliberate — stale tags keep filtering recommendations after a branch is reclassified — but it cannot be undone by switching the mode back."
          )}
        </p>
      </Modal>
    </>
  );
};

export default EnrichmentProfileForm;
